// platform-push-notifications: התראות דחיפה במכשיר הזה. מנוי = token של FCM על ה-SW שלנו (/push-sw.js), קשור בשרת לסשן
// (session_id שב-JWT). הפעלה רק בלחיצה (בקשת ההרשאה של הדפדפן). כיבוי/יציאה: מבטלים את ה-push subscription בדפדפן ומוחקים בשרת.
// firebase נטען רק בהפעלה (dynamic import) — לא בכניסה. בלי Analytics.
import { z } from 'zod';
import { authed, json } from './api.ts';

export const PushKind = z.enum(['turn', 'consent', 'request', 'trigger', 'share']);
export type PushKind = z.infer<typeof PushKind>;
const FirebaseWeb = z.object({ apiKey: z.string(), authDomain: z.string(), projectId: z.string(), storageBucket: z.string(), messagingSenderId: z.string(), appId: z.string() });
const PushInfo = z.object({
  config: z.union([z.object({ enabled: z.literal(false) }), z.object({ enabled: z.literal(true), vapidKey: z.string(), firebase: FirebaseWeb })]),
  subscribed: z.boolean().optional(),
  devices: z.number().optional(),
  prefs: z.record(PushKind, z.boolean()).optional(),
});
export type PushInfo = z.infer<typeof PushInfo>;

export const SW_PATH = '/push-sw.js';

export const pushApi = {
  info: () => json(PushInfo, '/api/push'),
  setPrefs: (p: Partial<Record<PushKind, boolean>>) =>
    json(z.object({ prefs: z.record(PushKind, z.boolean()) }), '/api/push/prefs', { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(p) }),
  test: () => json(z.object({ sent: z.number() }), '/api/push/test', { method: 'POST' }),
};

/** מה הדפדפן הזה יכול: unsupported (אין SW/Push — באייפון רק כשנוסף למסך הבית), denied (נחסם בהגדרות הדפדפן), ok. */
export function browserSupport(): 'unsupported' | 'denied' | 'ok' {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return Notification.permission === 'denied' ? 'denied' : 'ok';
}

/** אייפון/אייפד שלא נפתח מהמסך הבית — שם Web Push לא זמין. */
export function iosNeedsInstall(): boolean {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  return ios && !standalone;
}

/** הפעלה במכשיר הזה: הרשאה → SW → token של FCM → שמירה בשרת לסשן הזה. */
export async function enablePush(cfg: Extract<PushInfo['config'], { enabled: true }>): Promise<'on' | 'denied'> {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return 'denied';
  const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
  await navigator.serviceWorker.ready;
  const [{ initializeApp, getApps }, { getMessaging, getToken }] = await Promise.all([import('firebase/app'), import('firebase/messaging')]);
  const app = getApps()[0] ?? initializeApp(cfg.firebase);
  const token = await getToken(getMessaging(app), { vapidKey: cfg.vapidKey, serviceWorkerRegistration: reg });
  if (!token) throw new Error('no FCM token');
  const r = await authed('/api/push/subscriptions/current', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token }) });
  if (!r.ok) throw new Error(`subscribe ${r.status}`);
  return 'on';
}

/** ביטול ה-push subscription בדפדפן (ה-token של FCM מת). לא זורק. */
export async function dropBrowserSubscription(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration(SW_PATH);
    const sub = await reg?.pushManager.getSubscription();
    await sub?.unsubscribe();
  } catch {}
}

/** כיבוי במכשיר הזה. */
export async function disablePush(): Promise<void> {
  await dropBrowserSubscription();
  await authed('/api/push/subscriptions/current', { method: 'DELETE' });
}
